import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { UserPlus } from 'lucide-react';

export const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('rep');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('ref') || searchParams.get('invite');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await register(email, password, name, role, inviteToken);
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-[480px] bg-gradient-to-b from-slate-950 to-slate-900 flex-col justify-between p-12">
        <div>
          <h1 className="text-3xl font-black tracking-tighter text-white mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
            deal <span className="bg-gradient-to-r from-indigo-400 to-violet-400 bg-clip-text text-transparent">desk</span>
          </h1>
          <p className="text-slate-400 text-sm font-medium mt-1">manage your deals</p>
        </div>

        <div className="space-y-8">
          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3">Everything you need</p>
            <div className="space-y-4">
              {[
                'Full pipeline visibility with custom stages',
                'Partner management and bulk uploads',
                'Rich notes, templates, and data exports',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-violet-400 shrink-0" />
                  <p className="text-slate-300 text-sm leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <p className="text-xs text-slate-600">Built for modern sales teams</p>
      </div>

      {/* Right Form Panel */}
      <div className="flex-1 flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="lg:hidden mb-6">
              <h2 className="text-2xl font-black tracking-tighter text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                deal <span className="text-indigo-600">desk</span>
              </h2>
            </div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Create Account
            </h1>
            <p className="text-lg text-slate-500">Get started with deal desk</p>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-slate-200 shadow-sm">
            {inviteToken && (
              <div className="mb-5 p-3 bg-indigo-50 rounded-xl border border-indigo-100 text-center">
                <p className="text-sm font-semibold text-indigo-700">You've been invited by your manager</p>
                <p className="text-xs text-indigo-500 mt-0.5">Your account will be automatically linked when you register</p>
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name" className="text-xs font-bold uppercase tracking-wider text-slate-500">Full Name</Label>
                <Input
                  id="name"
                  data-testid="register-name-input"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="mt-2 rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">I am a</Label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    type="button"
                    data-testid="role-rep"
                    onClick={() => setRole('rep')}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-semibold transition-all ${
                      role === 'rep'
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    AE / Sales Rep
                  </button>
                  <button
                    type="button"
                    data-testid="role-manager"
                    onClick={() => setRole('manager')}
                    className={`px-4 py-3 rounded-lg border-2 text-sm font-semibold transition-all ${
                      role === 'manager'
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    Manager
                  </button>
                </div>
              </div>

              <div>
                <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
                <Input
                  id="email"
                  data-testid="register-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-2 rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  placeholder="you@company.com"
                />
              </div>

              <div>
                <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wider text-slate-500">Password</Label>
                <Input
                  id="password"
                  data-testid="register-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-2 rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                  placeholder="Create a password"
                />
              </div>

              {error && (
                <div data-testid="register-error-message" className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <Button
                data-testid="register-submit-button"
                type="submit"
                className="w-full bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg py-3 shadow-sm hover:shadow-md transition-all font-semibold text-base"
                disabled={loading}
              >
                <UserPlus size={18} strokeWidth={2} className="mr-2" />
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">
                Already have an account?{' '}
                <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-semibold">
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
